import * as Enumerable from "linq";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { bindable, Disposable } from "aurelia-framework";
import { EventAggregator } from "aurelia-event-aggregator";
import { autoinject, transient } from "aurelia-dependency-injection";
import { ListDisplayHelper } from "../../../helpers/list-display-helper";
import { Messages } from "../../../configuration/event-messages.constants";

@autoinject()
@transient()
export class GhgRatingSelector {

    isRatingPlus: boolean;
    isNotRatingPlus: boolean;
    isVerified: boolean;
    isUnverified: boolean;

    @bindable
    readonly: boolean;

    @bindable
    public selectedRatingWeights: number[] = [];

    @bindable
    public selectedItems: string[] = []; //used to apply selected ratings and isverified, rating plus, etc. when initialized

    @bindable
    public propertyName: string = "";

    @bindable
    public externalLabel: string;

    @bindable
    public startExternalLabel: string;

    @bindable
    public mobileTitle: string;

    @bindable
    public mobileDescription: string;

    @bindable
    public isMobile: boolean = false;

    @bindable
    public ratings: GHGRatingItem[] =
        [
            new GHGRatingItem("A", 80),
            new GHGRatingItem("B", 70),
            new GHGRatingItem("C", 60),
            new GHGRatingItem("D", 50),
            new GHGRatingItem("E", 40),
            new GHGRatingItem("U", 10, "N/A")
        ];

    @bindable
    public showActionButtons: boolean = true;

    selectAll: boolean;
    public isApplied: boolean = false;

    @bindable
    public closePopUpOnClear: boolean = true;
    
    private previousIsApplied: boolean;
    private previousSelectAll: boolean;
    private previousIsUnverified: boolean;
    private previousIsVerified: boolean;
    private previousIsRatingPlus: boolean;
    private previousIsNotRatingPlus: boolean;
    private previousSelectedRatingWeights: number[];
    private previousSelectedItems: string[];
    
    selectAllChanged(value: boolean) {
        if (value) {
            this.ratings.forEach(x => x.isSelected = true);
            this.isNotRatingPlus = true;
            this.isUnverified = true;
            this.isVerified = true;
            this.isRatingPlus = true;
        }
        else {
            this.ratings.forEach(x => x.isSelected = false);
            this.isNotRatingPlus = false;
            this.isUnverified = false;
            this.isVerified = false;
            this.isRatingPlus = false;
        }
        this.updateSelectedRatings();
    }

    constructor(private element: Element, private eventAggregator: EventAggregator) { }

    updateSelectedRatings() {
        let selectedRatings = this.ratings.filter(x => x.isSelected);
        const selectedRatingWeights = [];

        if (selectedRatings.length == 0 &&
            (this.isVerified || this.isUnverified ||
                this.isRatingPlus || this.isNotRatingPlus)) {
            selectedRatings = this.ratings;
        }

        for (const item of selectedRatings) {

            if (this.isVerified) {
                if (!this.isRatingPlus || this.isNotRatingPlus) {
                    selectedRatingWeights.push(item.weight + 4);
                }

                if (this.isRatingPlus || !this.isNotRatingPlus) {
                    selectedRatingWeights.push(item.weight + 6);
                }
            }

            if (this.isUnverified) {

                if (this.isRatingPlus) {
                    selectedRatingWeights.push(item.weight + 2);
                }

                if (this.isNotRatingPlus) {
                    selectedRatingWeights.push(item.weight);
                }

                if (!this.isRatingPlus && !this.isNotRatingPlus) {
                    selectedRatingWeights.push(item.weight);
                    selectedRatingWeights.push(item.weight + 2);
                }
            }

            if (!this.isVerified && !this.isUnverified && (this.isRatingPlus || this.isNotRatingPlus)) {
                if (this.isRatingPlus && !this.isNotRatingPlus) {
                    selectedRatingWeights.push(item.weight + 2);
                    selectedRatingWeights.push(item.weight + 6);
                } else if (this.isNotRatingPlus && !this.isRatingPlus) {
                    selectedRatingWeights.push(item.weight);
                    selectedRatingWeights.push(item.weight + 4);
                } else {
                    selectedRatingWeights.push(item.weight);
                    selectedRatingWeights.push(item.weight + 2);
                    selectedRatingWeights.push(item.weight + 4);
                    selectedRatingWeights.push(item.weight + 6);
                }
            }

            if (!this.isVerified &&
                !this.isUnverified &&
                !this.isNotRatingPlus &&
                !this.isRatingPlus) {
                selectedRatingWeights.push(item.weight);
                selectedRatingWeights.push(item.weight + 2);
                selectedRatingWeights.push(item.weight + 4);
                selectedRatingWeights.push(item.weight + 6);
            }

            if (this.isVerified) {
                selectedRatingWeights.push(item.weight + 1);
            }
            if (this.isRatingPlus) {
                selectedRatingWeights.push(item.weight + 3);
            }
            if (this.isUnverified) {
                selectedRatingWeights.push(item.weight + 5);
            }
            if (this.isNotRatingPlus) {
                selectedRatingWeights.push(item.weight + 7);
            }

            if (item.isSelected) {
                selectedRatingWeights.push(item.weight + 9);
            }
        }

        this.selectedRatingWeights = Enumerable.from(selectedRatingWeights).distinct().toArray();
        this.updateSelectedItems();
        this.updateExternalLabel();

        if (this.ratings.filter(x => !x.isSelected).length > 0) {
            this.selectAll = false;
        }
    }

    updateSelectedItems() {
        this.selectedItems = [];

        if (this.isVerified) {
            this.selectedItems.push("isVerified");
        }

        if (this.isUnverified) {
            this.selectedItems.push("isUnverified");
        }

        if (this.isRatingPlus) {
            this.selectedItems.push("isRatingPlus");
        }

        if (this.isNotRatingPlus) {
            this.selectedItems.push("isNotRatingPlus");
        }

        if (this.selectAll) {
            this.selectedItems.push("selectAll");
        }

        this.selectedItems = this.selectedItems.concat(this.ratings.filter(x => x.isSelected).map(x => x.rating));
    }

    setSelectedRatingsFromWeights() {
        const boolNames: string[] = [
            "selectAll",
            "isVerified",
            "isUnverified",
            "isRatingPlus",
            "isNotRatingPlus"
        ]

        if (this.selectedItems && this.selectedItems.length > 0) {
            this.isApplied = true; //set to true since there are already items selected, to updated the label since it is checking for is applied
            if (this.selectedItems.indexOf(boolNames[0]) > -1) {
                this.selectAll = true;
            }
            else {
                if (this.selectedItems.indexOf(boolNames[1]) > -1) {
                    this.isVerified = true;
                }
                if (this.selectedItems.indexOf(boolNames[2]) > -1) {
                    this.isUnverified = true;
                }
                if (this.selectedItems.indexOf(boolNames[3]) > -1) {
                    this.isRatingPlus = true;
                }
                if (this.selectedItems.indexOf(boolNames[4]) > -1) {
                    this.isNotRatingPlus = true;
                }

                const selectedRatings = this.selectedItems.filter(x => boolNames.indexOf(x) == -1);
                this.updateRatingModelsSelected(selectedRatings);
            }
        }

    }

    updateRatingModelsSelected(selectedRatings: string[]) {
        if (!selectedRatings) {
            this.ratings.forEach(x => {
                x.isSelected = false;
            });
        }
        else {
            this.ratings.forEach(x => {
                x.isSelected = selectedRatings.indexOf(x.rating) > -1;
            });
        }
    }

    selectedItemsChanged(value: string[]) {
        if(!value || value?.length === 0) {
            this.selectedRatingWeights = [];
        }
    }

    selectedRatingWeightsChanged() {
        setTimeout(() => {
            if (!this.selectedRatingWeights || this.selectedRatingWeights.length == 0) {
                this.selectAll = false;
                this.isNotRatingPlus = false;
                this.isRatingPlus = false;
                this.isUnverified = false;
                this.isVerified = false;
                this.updateRatingModelsSelected(null);
                this.updateExternalLabel();
            } else {
                this.ratings.forEach(x => x.isSelected = Enumerable.from(this.selectedRatingWeights).any(a => a == (x.weight + 9)));

                this.isVerified = this.selectedRatingWeights.filter(a => Number.isInteger((a - 1) / 10)).length > 0;
                this.isRatingPlus = this.selectedRatingWeights.filter(a => Number.isInteger((a - 3) / 10)).length > 0;
                this.isUnverified = this.selectedRatingWeights.filter(a => Number.isInteger((a - 5) / 10)).length > 0;
                this.isNotRatingPlus = this.selectedRatingWeights.filter(a => Number.isInteger((a - 7) / 10)).length > 0;

                this.updateExternalLabel();
            }
        });
    }

    attached() {
        setTimeout(() => {
            this.isOpen = true;
            this.previousRatings = CloneHelper.cloneObject(this.ratings);
            this.previousIsApplied = this.isApplied;
            this.previousSelectAll = this.selectAll;
            this.previousIsUnverified = this.isUnverified;
            this.previousIsVerified = this.isVerified;
            this.previousIsRatingPlus  = this.isRatingPlus;
            this.previousIsNotRatingPlus = this.isNotRatingPlus;
            this.previousSelectedRatingWeights = this.selectedRatingWeights;
            this.previousSelectedItems = this.selectedItems;
            this._subscriptionToDocumentClickEvent = this.eventAggregator.subscribe(Messages.documentClick, this.closePopup.bind(this));
        });
        this.setSelectedRatingsFromWeights();
        this.updateExternalLabel();
    }

    propertyNameChanged() {
        this.updateExternalLabel();
    }

    getSelectionDescription(isForMobile: boolean = false) {

        const ratings = this.ratings.map(x => { return {name: x.displayName, isSelected: x.isSelected};});
        if (this.isVerified) {
            ratings.unshift({name: "Verified", isSelected: true});
        }

        if (this.isUnverified) {
            ratings.unshift({name: "Unverified", isSelected: true});
        }

        if (this.isRatingPlus) {
            ratings.push({name: "Plus symbol", isSelected: true});
        }

        if (this.isNotRatingPlus) {
            ratings.push({name: "No plus symbol", isSelected: true});
        }
        
        return isForMobile? ListDisplayHelper.getSelectableListToString(ratings)
            : (this.startExternalLabel ? `${this.startExternalLabel}` : `GHG Rating`);
    }

    updateExternalLabel() {
        if (Enumerable.from(this.ratings).any(x => x.isSelected) || this.isVerified || this.isUnverified || this.isRatingPlus || this.isNotRatingPlus) {
            this.externalLabel = this.getSelectionDescription();
            this.mobileTitle = this.propertyName;
            this.mobileDescription = this.getSelectionDescription(true);
        }
        else {
            this.externalLabel = "";
            this.mobileTitle = this.propertyName;
            this.mobileDescription = "";
        }
    }

    public clear() {
        this.updateRatingModelsSelected(null);
        this.clearAll();
        
        if(this.closePopUpOnClear){
            setTimeout(() => {
                this.applyFilters(false);
            }, 120);   
        }
        
        this.updateExternalLabel();
    }

    public previousRatings: GHGRatingItem[]

    public applyFilters(closeFilter: boolean = true, reset: boolean = false) {
        if (reset) {
            this.reset();
        }

        this.isApplied = Enumerable.from(this.ratings).any(x => x.isSelected);
        this.updateExternalLabel();
        const changeEvent: CustomEvent = new CustomEvent("apply-filters",
            {
                bubbles: true,
                detail: {
                    closeFilter: closeFilter,
                    filters: this.selectedRatingWeights
                }
            });
        this.element.dispatchEvent(changeEvent);
    }

    _subscriptionToDocumentClickEvent: Disposable;

    detached() {
        if (this._subscriptionToDocumentClickEvent)
            this._subscriptionToDocumentClickEvent.dispose();
    }

    isOpen: boolean;
    closePopup(x: any) {
        if(this.isMobile) return;
        if (x && x.source == 'document' && this.isOpen) {
            this.isOpen = false;
            this.applyFilters(true, this.showActionButtons) 
            
        }
    }

    reset() {
        this.ratings = this.previousRatings;
        this.isApplied = this.previousIsApplied;
        this.selectAll = this.previousSelectAll;
        this.isUnverified = this.previousIsUnverified;
        this.isVerified = this.previousIsVerified;
        this.isRatingPlus = this.previousIsRatingPlus;
        this.isNotRatingPlus = this.previousIsNotRatingPlus;
        this.selectedRatingWeights = this.previousSelectedRatingWeights;
        this.selectedItems = this.previousSelectedItems;
}
    
    clearAll() {
        this.isApplied = false;
        this.selectAll = false;
        this.isUnverified = false;
        this.isVerified = false;
        this.isRatingPlus = false;
        this.isNotRatingPlus = false;
        this.selectedRatingWeights = [];
        this.selectedItems = [];
    }

}

export class GHGRatingItem {
    rating: string;
    displayName: string;
    weight: number;
    isSelected?: boolean = false;

    constructor(rating: string, weight: number, displayName: string = null) {
        this.rating = rating;
        this.weight = weight;
        this.displayName = displayName ? displayName : rating;
    }

    className?: () => string = () => {
        return this.displayName.toLowerCase().trim().replace("/", "").replace(" ", "-");
    }
}
